import Link from "next/link";
import { DeFiRow } from "./type";
import { Card, Typography } from "@kelp-dao/kernel-design-system";
import {
  DEFI_ADD_LIQUIDITY,
  DEFI_WITHDRAW,
  // FEATURED_OPPORTUNITIES,
  // GENERAL_OPPORTUNITIES,
} from "@constants/analytics";
import { trackEvent } from "utils";

type Props = {
  tokenName: string;
  mainImg?: string;
  overlayTokenImg?: string;
  icon?: string;
  chip1Text?: string;
  chip2Text?: string;
  tvl?: string;
  apy?: string;
  row: DeFiRow;
  featuredDefi?: boolean;
};

const DefiOpportunitiesCard = ({
  tokenName,
  mainImg,
  overlayTokenImg,
  icon,
  chip1Text,
  chip2Text,
  tvl,
  apy,
  row,
  featuredDefi = false,
}: Props) => {
  const isOlive = row.Provider?.toLowerCase() === "olive";
  const href = isOlive
    ? "https://www.olivenetwork.xyz/stake"
    : row["Add Liquidity"];

  const handleClick = () => {
    if (!href) return;
    trackEvent(
      isOlive ? DEFI_WITHDRAW : DEFI_ADD_LIQUIDITY,
      isOlive
        ? {
            chain: row.Section,
            activity_type: row.Activity,
            defi_protocol: row.Provider,
            asset: row.Pair,
            source: featuredDefi
              ? "top_opportunities"
              : "general_opportunities",
          }
        : {
            defi_chain: row.Section,
            activity_type: row.Activity,
            defi_protocol: row.Provider,
            asset: row.Pair,
            source: featuredDefi
              ? "top_opportunities"
              : "general_opportunities",
          },
    );
  };

  const cardContent = (
    <Card
      variant="non-bordered"
      className={`w-full rounded-xl bg-white px-4 py-4 lg:p-2 xl:px-4 xl:py-5 ${
        href ? "cursor-pointer transition-opacity hover:opacity-90" : ""
      }`}
    >
      <div className="flex gap-3">
        <div className="flex shrink-0 items-center h-6">
          {overlayTokenImg && (
            <img
              src={overlayTokenImg}
              alt="side token"
              className="h-6 w-6 rounded-full"
            />
          )}
          {mainImg && (
            <img
              src={mainImg}
              alt="main token"
              className="h-6 w-6 rounded-full ml-[-10px]"
            />
          )}
        </div>
        {/* Header with logos + text + icon */}
        <div className="flex flex-col gap-4 grow">
          <div className="flex items-center justify-between">
            <div className="flex w-full items-center justify-start gap-4">
              {/* Token + overlay image */}

              <Typography
                as="h1"
                type="heading"
                variant="xxsmall"
                className="font-sans"
              >
                {tokenName}
              </Typography>
            </div>
            {icon && <img src={icon} alt="icon" className="w-6 h-6" />}
          </div>

          {/* Buttons */}
          <div className="flex gap-[10px]">
            {chip1Text && (
              <Typography
                as="span"
                type="body"
                variant="xsmall"
                className="bg-bg-primary px-2 py-1 rounded-md"
              >
                {chip1Text}
              </Typography>
            )}
            {chip2Text && (
              <Typography
                as="span"
                type="body"
                variant="xsmall"
                className="bg-bg-primary px-2 py-1 rounded-md"
              >
                {chip2Text.length > 10 && chip1Text
                  ? chip2Text.slice(0, 10) + "..."
                  : chip2Text}
              </Typography>
            )}
          </div>

          {/* Divider */}
          <hr className="m-0 h-px w-full shrink-0 border-0 bg-[#CCE5E5]" />

          {/* Stats */}
          <div className="flex flex-col gap-2">
            <div className="flex items-center justify-between text-sm text-gray-600 w-full">
              <Typography
                type="body"
                variant="small"
                className="text-text-secondary text-xs font-normal"
              >
                TVL
              </Typography>
              <Typography
                type="body"
                variant="small"
                className="font-normal text-text-primary text-xs"
              >
                {tvl}
              </Typography>
            </div>
            <div className="flex items-center justify-between text-sm text-gray-600 w-full">
              <Typography
                type="body"
                variant="small"
                className="text-text-secondary text-xs font-normal"
              >
                APY
              </Typography>
              <Typography
                type="body"
                variant="small"
                className="font-normal text-text-primary text-xs"
              >
                {apy}
              </Typography>
            </div>
          </div>
        </div>
      </div>
    </Card>
  );

  if (href) {
    return (
      <Link
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        onClick={handleClick}
        className="block w-full"
      >
        {cardContent}
      </Link>
    );
  }

  return cardContent;
};

export default DefiOpportunitiesCard;
